import React, { useEffect, useMemo } from 'react';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Badge, Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, makeStyles, OverlayDrawer, Text, tokens } from '@fluentui/react-components';
import { connect } from 'react-redux';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

// component functions
import * as ComponentFunctions from './StageFix.Form.Functions';

// models
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';

// components
import StageFixFormItemComponent from './StageFixFormItem/StageFix.Form.Item.Component';

const UNASSIGNED_BRANCH_KEY = 'unassigned';

interface BranchGroup {
    key: string;
    branchId?: number;
    label: string;
    stages: ContractStage[];
}

const StageFixFormComponent = (props: ComponentFunctions.Props) => {

    const styles = useStyles();
    const { t } = useTranslation();

    //load necesary data
    useEffect(() => {
        props.Protocol?.id && props.GetMedicalProtocolById(props.Protocol?.id);
    }, [props.Protocol?.id === undefined]);

    // save control
    useEffect(() => {
        props.GetVisitFullByProtocolId(props.ProtocolId);
    }, [props.Protocol?.id !== undefined]);

    // branches used to group stages/visits
    useEffect(() => {
        if (props.Protocol?.contractAssociation?.contractId) {
            props.GetContractById(props.Protocol.contractAssociation.contractId);
            props.GetAllContractsBranchs(props.Protocol.contractAssociation.contractId);
        }
    }, [props.Protocol?.contractAssociation?.contractId]);

    // Contract.stages' reducer no-ops unless the Contract already exists in state, so this must wait for GetContractById above to land.
    useEffect(() => {
        props.Contract?.id && props.GetAllContractsStage(props.Contract.id);
    }, [props.Contract?.id]);

    const HandleCancelForm = () => {
        props.dismissPanel();
    };

    const contractBranches = useMemo(() => {
        const activeAddendum = props.Contract?.addendums?.find((addendum) => addendum.isActive)
            || props.Contract?.addendums?.[0];
        const activeAddendumId = activeAddendum?.id;

        const branchesFromActiveAddendum = activeAddendum?.branches || [];
        const branchesFromContract = activeAddendumId
            ? (props.Contract?.branches || []).filter((branch) => branch.contractAddendumId === activeAddendumId)
            : [];
        const branchesFromStore = activeAddendumId
            ? (props.ContractBranches || []).filter((branch) => branch.contractAddendumId === activeAddendumId)
            : (props.ContractBranches || []);

        return [...branchesFromActiveAddendum, ...branchesFromContract, ...branchesFromStore]
            .filter((branch, index, array) =>
                index === array.findIndex((candidate) => candidate.id === branch.id)
            );
    }, [props.Contract, props.ContractBranches]);

    const branchGroups = useMemo<BranchGroup[]>(() => {
        // the visits-full payload used for `Stages` does not reliably carry branchId,
        // so resolve it from the authoritative Contract.stages list when available.
        const stageBranchMap = new Map<number, number | null | undefined>(
            (props.Contract?.stages || []).map((stage) => [stage.id, stage.branchId])
        );

        const stages = (props.Stages || []).map((stage) => ({
            ...stage,
            branchId: stageBranchMap.has(stage.id) ? stageBranchMap.get(stage.id) : stage.branchId
        }));
        const unassignedStages = stages.filter((stage) => stage.branchId === undefined || stage.branchId === null);

        const groupsByBranchId = new Map<number, ContractStage[]>();
        stages.forEach((stage) => {
            if (stage.branchId === undefined || stage.branchId === null) {
                return;
            }
            const stagesForBranch = groupsByBranchId.get(stage.branchId) || [];
            stagesForBranch.push(stage);
            groupsByBranchId.set(stage.branchId, stagesForBranch);
        });

        const knownBranchIds = new Set(contractBranches.map((branch: ContractBranch) => branch.id));
        const orderedBranchIds = [
            ...contractBranches.map((branch: ContractBranch) => branch.id),
            ...Array.from(groupsByBranchId.keys()).filter((branchId) => !knownBranchIds.has(branchId))
        ];

        const branchGroupsList: BranchGroup[] = orderedBranchIds
            .filter((branchId) => groupsByBranchId.has(branchId))
            .map((branchId) => {
                const branch = contractBranches.find((candidate: ContractBranch) => candidate.id === branchId);
                return {
                    key: `branch-${branchId}`,
                    branchId,
                    label: branch?.branchName || t('stageList.props.unknownBranch', { branchId, defaultValue: 'Rama {{branchId}}' }),
                    stages: groupsByBranchId.get(branchId) || []
                };
            });

        return [
            {
                key: UNASSIGNED_BRANCH_KEY,
                branchId: undefined,
                label: t('stageList.props.unassignedBranch', 'Sin rama'),
                stages: unassignedStages
            },
            ...branchGroupsList
        ];
    }, [props.Stages, props.Contract?.stages, contractBranches, t]);

    const renderStages = (stages: ContractStage[]) => stages.map((stage: ContractStage, index: number) => {
        const excludedVisitIds = stages
            .filter((sibling) => sibling.id !== stage.id && sibling.emrProtocolVisitIdRelated !== undefined)
            .map((sibling) => sibling.emrProtocolVisitIdRelated as number);

        return (
            <StageFixFormItemComponent
                key={`${stage.id}-${index}`}
                ProtocolId={props.ProtocolId}
                StageId={stage.id}
                ExcludedVisitIds={excludedVisitIds}
            />
        );
    });

    const hasBranchGroups = branchGroups.some((group) => group.branchId !== undefined);

    return (
        <OverlayDrawer open={props.isOpen} position={"end"} size='medium'>
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            aria-label={t('stageFixForm.actions.close', 'Cerrar')}
                            appearance="subtle"
                            icon={<Dismiss24Regular />}
                            onClick={HandleCancelForm}
                        />
                    }
                >
                    {t('stageFixForm.header', 'Visitas protocolares por etapa')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <div className={styles.body}>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryText}>
                            <Text className={styles.summaryTitle}>
                                {t('stageFixForm.summary.title', 'Asociación de visitas del protocolo')}
                            </Text>
                            <Text className={styles.summaryDescription}>
                                {t('stageFixForm.summary.description', 'Cada etapa permite una única visita, pero una misma visita puede repetirse en distintas etapas.')}
                            </Text>
                        </div>
                        <Badge appearance="filled" color="brand" size="large">
                            {t('stageFixForm.summary.totalStages', { count: props.Stages?.length ?? 0, defaultValue: '{{count}} etapas' })}
                        </Badge>
                    </div>
                    {hasBranchGroups ? (
                        <Accordion multiple collapsible defaultOpenItems={branchGroups.map((group) => group.key)}>
                            {branchGroups.map((group) => (
                                <AccordionItem key={group.key} value={group.key}>
                                    <AccordionHeader expandIconPosition="end">
                                        <div className={styles.groupHeader}>
                                            <Text weight="semibold">{group.label}</Text>
                                            <Badge appearance="tint" color={group.branchId ? 'brand' : 'informative'}>
                                                {group.stages.length}
                                            </Badge>
                                        </div>
                                    </AccordionHeader>
                                    <AccordionPanel>
                                        <div className={styles.stageList}>
                                            {group.stages.length > 0
                                                ? renderStages(group.stages)
                                                : <Text className={styles.summaryDescription}>
                                                    {t('stageFixForm.branchGroup.empty', 'No hay etapas asignadas a esta rama.')}
                                                </Text>
                                            }
                                        </div>
                                    </AccordionPanel>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    ) : (
                        <div className={styles.stageList}>
                            {renderStages(props.Stages || [])}
                        </div>
                    )}
                </div>
            </DrawerBody>
            <DrawerFooter>
                <div className={styles.footer}>
                    <Button onClick={HandleCancelForm} appearance="secondary">
                        {t('stageFixForm.actions.close', 'Cerrar')}
                    </Button>
                </div>
            </DrawerFooter>

        </OverlayDrawer >
    );
};

export default connect (
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
) (StageFixFormComponent as any);

const useStyles = makeStyles({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        width: '100%',
    },
    summaryCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground2,
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingHorizontalM,
    },
    summaryText: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    summaryTitle: {
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    summaryDescription: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        color: tokens.colorNeutralForeground3,
    },
    stageList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        paddingTop: tokens.spacingVerticalS,
    },
    groupHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: tokens.spacingHorizontalM,
    },

    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
    },
});